import { FETCH_DATA_SOURCES_FAILURE, FETCH_DATA_SOURCES_REQUEST } from '../actions/AIStudioAction';
import {
  DATA_SOURCE_ADD_COMPLETED,
  DATA_SOURCE_ADD_CONNECTED,
  DATA_SOURCE_ADD_FAILED,
  DATA_SOURCE_ADD_STEP,
  DATA_SOURCE_ADD_STEPS,
  DATA_SOURCE_CREATE_COMPLETED,
  DATA_SOURCE_CREATE_CONNECTED,
  DATA_SOURCE_CREATE_FAILED,
  DATA_SOURCE_CREATE_RESET,
  DATA_SOURCE_CREATE_STARTED,
  DATA_SOURCE_CREATE_STEP,
  DATA_SOURCE_CREATE_STEPS,
  SET_DATA_SOURCES,
} from '../types';

type UiStatus = 'Pending' | 'In Progress' | 'Completed' | 'Failed';
const FROM_RAW: Record<string, UiStatus> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  failed: 'Failed',
};

interface IndexState {
  isConnecting: boolean;
  connected: boolean;
  steps: { id: string; label: string; status: UiStatus; messages: string[] }[];
  complete: boolean;
  ok: boolean | null;
  summary?: string;
  startedAt?: number;
  indexes: any[];
  total: number;
  currentPage: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
}

const initial: IndexState = {
  isConnecting: false,
  connected: false,
  steps: [],
  complete: false,
  ok: null,
  indexes: [],
  total: 0,
  currentPage: 1,
  totalPages: 1,
  loading: false,
  error: null,
};

export function aistudioReducer(state = initial, action: any): IndexState {
  switch (action.type) {
    case FETCH_DATA_SOURCES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
  case SET_DATA_SOURCES:
      return {
        ...state,
        loading: false,
        indexes: action.payload.indexes,
        total: action.payload.total,
        currentPage: action.payload.currentPage,
        totalPages: action.payload.totalPages,
      };
  case FETCH_DATA_SOURCES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case DATA_SOURCE_CREATE_STARTED:
      return { ...state, connected: false, complete: false, ok: null, startedAt: action.payload.startedAt };
    case DATA_SOURCE_CREATE_CONNECTED:
      return { ...state, connected: true };
    case DATA_SOURCE_CREATE_STEPS:
      return {
        ...state,
        steps: (action.payload?.steps || []).map((s: any) => ({
          id: s.id,
          label: s.label,
          status: FROM_RAW[s.status] || 'Pending',
          messages: [],
        })),
      };
    case DATA_SOURCE_CREATE_STEP: {
      const { id, status, message } = action.payload || {};
      const ui = FROM_RAW[status] || 'Pending';
      const steps = [...state.steps];
      const i = steps.findIndex(s => s.id === id);
      if (i === -1) {
        steps.push({ id, label: id, status: ui, messages: message ? [message] : [] });
      } else {
        // Replace message instead of accumulating - show only current progress
        steps[i] = {
          ...steps[i],
          status: ui,
          messages: message ? [message] : steps[i].messages,
        };
      }
      return { ...state, steps };
    }
    case DATA_SOURCE_CREATE_COMPLETED:
      return {
        ...state,
        complete: true,
        ok: true,
        summary: 'Data source created successfully',
      };
    case DATA_SOURCE_CREATE_FAILED:
      return {
        ...state,
        complete: true,
        steps: [],
        ok: false,
        summary: action.payload?.error || 'Failed',
      };
    case DATA_SOURCE_CREATE_RESET:
      return {
        ...state,
        isConnecting: false,
        connected: false,
        steps: [],
        complete: false,
        ok: null,
        summary: undefined,
        startedAt: undefined,
      };
    case DATA_SOURCE_ADD_CONNECTED:
      return { ...state, connected: true };
    case DATA_SOURCE_ADD_STEPS:
      return {
        ...state,
        steps: (action.payload?.steps || []).map((s: any) => ({
          id: s.id,
          label: s.label,
          status: FROM_RAW[s.status] || 'Pending',
          messages: [],
        })),
      };
    case DATA_SOURCE_ADD_STEP: {
      const { id, status, message } = action.payload || {};
      const ui = FROM_RAW[status] || 'Pending';
      const steps = [...state.steps];
      const i = steps.findIndex(s => s.id === id);
      if (i === -1) {
        steps.push({ id, label: id, status: ui, messages: message ? [message] : [] });
      } else {
        // Replace message instead of accumulating - show only current progress
        steps[i] = {
          ...steps[i],
          status: ui,
          messages: message ? [message] : steps[i].messages,
        };
      }
      return { ...state, steps };
    }
    case DATA_SOURCE_ADD_COMPLETED:
      return {
        ...state,
        steps: [],
        complete: true,
        isConnecting: false,
        ok: action.payload?.ok,
        summary: action.payload?.message || 'Failed',
      };
    case DATA_SOURCE_ADD_FAILED:
      return {
        ...state,
        complete: true,
        isConnecting: false,
        steps: [],
        ok: false,
        summary: action.payload?.error || 'Failed',
      };
    default:
      return state;
  }
}